/**
 * QuestionEditForm — admin/teacher form for editing a single question.
 *
 * Acts as the form shell: owns the entire `form` state, dirty tracking,
 * top action bar (Fix LaTeX / Publish / Cancel / Save), the section
 * composition, and the version-history panel below.
 *
 * The four content sections live in `./sections/`:
 *   - MetadataSection           (subject/topic/type/diff/marks/...)
 *   - QuestionContentSection    (text/diagram/parts; renders MCQOptionsEditor)
 *   - SolutionsHintsSection     (answer/solutions/hints/keywords/...)
 *   - RelatedQuestionsSection   (admin only — picker modal + list)
 *
 * Split from a single ~1200-line file on 2026-05-17 to make the form
 * sections independently editable (Stage 2 of the QuestionsPage refactor).
 *
 * Public API (`QuestionEditForm`, `QuestionFormData`, `QuestionEditFormProps`)
 * is unchanged; consumers in admin-console, teacher-app, and paper-import
 * continue to import as before.
 */
import { useState, useRef, useEffect } from 'react';
import type { ExamAppearance, Solution } from '../questions-page';
import type { ModuleItem } from '../reference-data/types';
import {
    SOLUTION_STYLE_OPTIONS,
} from '../question-set-types';
import { SUBJECT_OPTIONS, QUESTION_SUBJECT_OPTIONS } from '@padcanvas/shared';
import type { QuestionPart } from '@padcanvas/shared';
import { QuestionHistoryPanel } from '../question-history';
import type { QuestionHistoryApi } from '../question-history';
import {
    nextPartId,
    type ConceptOption,
    type BookOption,
    type QuestionFormData,
    type UpdateField,
} from './formTypes';
import { MetadataSection } from './sections/MetadataSection';
import { QuestionContentSection } from './sections/QuestionContentSection';
import { SolutionsHintsSection } from './sections/SolutionsHintsSection';
import { RelatedQuestionsSection } from './sections/RelatedQuestionsSection';
import type { QuestionImageSlot } from '@padcanvas/shared';
import { SourceCropModal } from '../image-editor';
import {
    QuestionSourceContext, applySlotImage, regionForSlot,
    type QuestionSourceApi, type QuestionSourceContextValue, type QuestionSourceInfo,
} from './questionSource';
import './QuestionEditForm.css';

// Public re-exports kept for back-compat with consumers that imported these
// from `./QuestionEditForm` directly.
export type { QuestionFormData } from './formTypes';
export type { QuestionPart } from '@padcanvas/shared';

export interface QuestionEditFormProps {
    initialData: QuestionFormData;
    onSave: (data: QuestionFormData) => void;
    onCancel: () => void;
    saving?: boolean;
    /** Why the last save failed, shown beside the button that failed.
     *
     *  The server rejects a question for reasons the form cannot always predict
     *  — an unfiled module, a part missing its answer — and without somewhere to
     *  put that, Save simply did nothing: the dialog stayed open, the caller's
     *  promise rejected into the void, and the reader was left pressing a button
     *  that appeared to be ignoring them. */
    saveError?: string | null;
    modulesBySubject?: Record<string, ModuleItem[]>;
    /** Show admin-only features: Fix LaTeX, images, readiness, enrichment, related questions, etc. */
    isAdmin?: boolean;
    /** Restrict subject list to these subjects (e.g. teacher's allowed subjects) */
    allowedSubjects?: string[];
    onPublish?: () => void;
    showPublish?: boolean;
    onUnpublish?: () => void;
    showUnpublish?: boolean;
    questionId?: string;
    /** Teacher email to display (read-only banner). Resolved by caller. */
    teacherEmail?: string;
    teacherId?: string;
    sourceImportId?: string;
    importBatchId?: string;
    onDirtyChange?: (dirty: boolean) => void;
    formDataRef?: React.MutableRefObject<QuestionFormData | null>;
    renderTopCenter?: () => React.ReactNode;
    /** Admin callback to fix LaTeX in question content */
    onFixLatex?: (data: {
        questionText: string;
        options?: { label: string; text: string }[];
        solutions?: { style?: string; proficiencyLevel?: string; steps: string; expectedTimeSeconds?: number }[];
        hints?: string[];
        correctAnswer: string;
    }) => Promise<{
        questionText?: string;
        correctAnswer?: string;
        options?: { text: string }[];
        solutions?: { steps: string }[];
        hints?: string[];
    }>;
    /** Admin callback to upload an image and return its URL */
    onUploadImage?: (base64: string, mimeType: string) => Promise<{ url: string }>;
    /** Admin callback to fetch a question by ID (for related questions) */
    onGetQuestionById?: (id: string) => Promise<{ questionText: string }>;
    /** Admin: render question picker modal content */
    renderQuestionPicker?: (props: { selectedIds: string[]; onSelectionChange: (ids: string[]) => void }) => React.ReactNode;
    /** Builds a URL for a related question, opened in a new tab from the
     *  Related Questions list. Omitted ⇒ rows render without an open link. */
    questionHref?: (id: string) => string;
    /** Admin: render a live "which curricula will this be part of?" panel from the
     *  current (unsaved) module / class / target-exam values. Shown in the Metadata
     *  section just above "Appeared In Exams". When omitted, nothing renders. */
    renderCurriculaPreview?: (args: { moduleId?: string; classes?: string[]; targetExams?: string[] }) => React.ReactNode;
    /** Admin: load the concept registry for a subject (powers the Phase 2
     *  concept-tagging pickers). When omitted, the pickers are hidden. */
    onLoadConcepts?: (subject: string) => Promise<ConceptOption[]>;
    /** Admin: load the book registry for the textbook-source picker. When
     *  omitted, the book picker is hidden (sourceType select still shows). */
    onLoadBooks?: () => Promise<BookOption[]>;
    /** When provided, renders the version history panel below the form. */
    historyApi?: QuestionHistoryApi;
    /** Called after a successful restore, so the parent can reload the question. */
    onHistoryRestored?: () => void;
    /** Re-cut images from the paper the question was extracted from. Offered on
     *  every image field when the question has a paper on record.
     *  See docs/FeatureDesigns/question-image-recrop-target.md. */
    sourceApi?: QuestionSourceApi;
}

export function QuestionEditForm({
    initialData, onSave, onCancel, saving, saveError, modulesBySubject,
    isAdmin, allowedSubjects,
    onPublish, showPublish, onUnpublish, showUnpublish,
    questionId,
    teacherEmail, teacherId,
    onDirtyChange, formDataRef, renderTopCenter,
    onFixLatex, onUploadImage, onGetQuestionById, renderQuestionPicker, questionHref,
    renderCurriculaPreview,
    onLoadConcepts, onLoadBooks,
    historyApi, onHistoryRestored,
    sourceApi,
}: QuestionEditFormProps) {
    // ── Form state + dirty tracking ──────────────────────────────────
    const [form, setForm] = useState<QuestionFormData>(() => ({
        ...initialData,
        options: initialData.options || [],
        solutions: initialData.solutions || [],
        hints: initialData.hints || [],
        appearedInExams: initialData.appearedInExams || [],
        targetExams: initialData.targetExams || [],
        relatedQuestions: initialData.relatedQuestions || [],
        parts: initialData.parts || [],
    }));

    const initialSnapshot = useRef(JSON.stringify({
        ...initialData,
        options: initialData.options || [],
        solutions: initialData.solutions || [],
        hints: initialData.hints || [],
        appearedInExams: initialData.appearedInExams || [],
        targetExams: initialData.targetExams || [],
        relatedQuestions: initialData.relatedQuestions || [],
        parts: initialData.parts || [],
    }));

    useEffect(() => {
        if (formDataRef) formDataRef.current = form;
        if (!onDirtyChange) return;
        const dirty = JSON.stringify(form) !== initialSnapshot.current;
        onDirtyChange(dirty);
    }, [form, onDirtyChange, formDataRef]);

    const [fixingLatex, setFixingLatex] = useState(false);

    // ── Source paper ("Crop from paper") ──
    // Loaded once per question; an unavailable source simply hides the buttons.
    const [source, setSource] = useState<QuestionSourceInfo | null>(null);
    const [cropSlot, setCropSlot] = useState<QuestionImageSlot | null>(null);
    useEffect(() => {
        setSource(null);
        if (!sourceApi || !questionId) return;
        let cancelled = false;
        sourceApi.getSource(questionId)
            .then(info => { if (!cancelled) setSource(info); })
            .catch(() => { /* no paper to offer — the fields keep their other tools */ });
        return () => { cancelled = true; };
    }, [sourceApi, questionId]);
    const sourceContext: QuestionSourceContextValue = {
        available: !!source?.available,
        openCrop: setCropSlot,
    };

    // ── Concept registry for the current subject (Phase 2 tagging) ──
    // Loaded lazily per subject; pickers are admin-only and hidden when the
    // host doesn't supply onLoadConcepts.
    const [conceptOptions, setConceptOptions] = useState<ConceptOption[]>([]);
    useEffect(() => {
        if (!onLoadConcepts || !form.subject) { setConceptOptions([]); return; }
        let cancelled = false;
        onLoadConcepts(form.subject)
            .then(opts => { if (!cancelled) setConceptOptions(opts); })
            .catch(() => { if (!cancelled) setConceptOptions([]); });
        return () => { cancelled = true; };
    }, [onLoadConcepts, form.subject]);

    // ── Book registry for the textbook-source picker ──
    // Loaded once (admin-only, hidden when the host doesn't supply onLoadBooks).
    const [bookOptions, setBookOptions] = useState<BookOption[]>([]);
    useEffect(() => {
        if (!onLoadBooks) { setBookOptions([]); return; }
        let cancelled = false;
        onLoadBooks()
            .then(opts => { if (!cancelled) setBookOptions(opts); })
            .catch(() => { if (!cancelled) setBookOptions([]); });
        return () => { cancelled = true; };
    }, [onLoadBooks]);

    const handleFixLatex = async () => {
        if (!onFixLatex) return;
        setFixingLatex(true);
        try {
            const result = await onFixLatex({
                questionText: form.questionText,
                options: form.options?.map(o => ({ label: o.label, text: o.text })),
                solutions: form.solutions?.map(s => ({ style: s.style, proficiencyLevel: s.proficiencyLevel, steps: s.steps, expectedTimeSeconds: s.expectedTimeSeconds })),
                hints: form.hints,
                correctAnswer: form.correctAnswer,
            });
            setForm(prev => ({
                ...prev,
                ...(result.questionText !== undefined && { questionText: result.questionText }),
                ...(result.correctAnswer !== undefined && { correctAnswer: result.correctAnswer }),
                ...(result.options && { options: prev.options?.map((o, i) => ({ ...o, text: result.options![i]?.text ?? o.text })) }),
                ...(result.solutions && { solutions: prev.solutions?.map((s, i) => ({ ...s, steps: result.solutions![i]?.steps ?? s.steps })) }),
                ...(result.hints && { hints: result.hints }),
            }));
        } catch (err) {
            alert('Fix LaTeX failed: ' + (err instanceof Error ? err.message : String(err)));
        } finally {
            setFixingLatex(false);
        }
    };

    // ── Field-level updater used by every section ────────────────────
    const updateField: UpdateField = (field, value) => {
        setForm(prev => {
            const next = { ...prev, [field]: value };
            // On a type switch, drop fields that don't belong to the new type so
            // stale options/parts/errorMargin/marks aren't shown or saved. The
            // backend also sanitizes (see sanitizeQuestionByType), but clearing
            // here keeps the editor consistent and avoids sending them.
            if (field === 'questionType') {
                const t = value as string | undefined;
                if (t !== 'MCQ' && t !== 'MCQ_MULTI') delete next.options;
                if (t !== 'NUMERICAL') delete next.errorMargin;
                if (t === 'STRUCTURED') { delete next.marks; } else { delete next.parts; delete next.requirement; }
            }
            return next;
        });
    };

    // STRUCTURED choice / K-of-N expression (Phase 1C). The editor emits a
    // validated requirement, or undefined for "All parts".
    const onRequirementChange = (requirement: QuestionFormData['requirement']) => {
        setForm(prev => {
            const next = { ...prev };
            if (requirement) next.requirement = requirement; else delete next.requirement;
            return next;
        });
    };

    // ── Solution handlers (state owned here; section is presentational) ──
    const handleSolutionChange = (index: number, field: keyof Solution, value: string | number | undefined) => {
        const solutions = [...(form.solutions || [])];
        solutions[index] = { ...solutions[index], [field]: value };
        setForm(prev => ({ ...prev, solutions }));
    };

    const addSolution = () => {
        const solutions = [...(form.solutions || [])];
        const usedStyles = solutions.map(s => s.style || s.proficiencyLevel);
        const availableStyle = SOLUTION_STYLE_OPTIONS.find(s => !usedStyles.includes(s)) || 'formal';
        solutions.push({ style: availableStyle, steps: '' });
        setForm(prev => ({ ...prev, solutions }));
    };

    const removeSolution = (index: number) => {
        const solutions = [...(form.solutions || [])];
        solutions.splice(index, 1);
        setForm(prev => ({ ...prev, solutions }));
    };

    // methodConcepts is string[] (not a scalar like the other solution fields),
    // so it gets its own handler rather than going through handleSolutionChange.
    const handleSolutionConceptsChange = (index: number, ids: string[]) => {
        const solutions = [...(form.solutions || [])];
        solutions[index] = { ...solutions[index], methodConcepts: ids };
        setForm(prev => ({ ...prev, solutions }));
    };

    // ── STRUCTURED Part handlers ──
    // Parts are lettered sub-parts under a single question number. partIds
    // are stable across edits — removing a part does NOT shift others' ids;
    // re-adding picks the first free letter. See question-structure.md.
    const addPart = () => {
        setForm(prev => {
            const parts = [...(prev.parts || [])];
            parts.push({
                partId: nextPartId(parts),
                type: 'FREEFORM',
                statement: '',
            });
            return { ...prev, parts };
        });
    };

    const removePart = (index: number) => {
        setForm(prev => {
            const parts = [...(prev.parts || [])];
            parts.splice(index, 1);
            return { ...prev, parts };
        });
    };

    const updatePart = <K extends keyof QuestionPart>(index: number, field: K, value: QuestionPart[K]) => {
        setForm(prev => {
            const parts = [...(prev.parts || [])];
            parts[index] = { ...parts[index], [field]: value };
            // Type change sanitization (Phase 1B): per-type fields don't
            // survive a type switch — stale options/errorMargin would
            // otherwise be saved (backend also strips, but don't rely on it).
            if (field === 'type') {
                const t = value as QuestionPart['type'];
                if (t !== 'MCQ' && t !== 'MCQ_MULTI') delete parts[index].options;
                if (t !== 'NUMERICAL') delete parts[index].errorMargin;
                // correctAnswer semantics differ per type (option labels vs
                // number vs free text) — clear it so the author re-enters.
                delete parts[index].correctAnswer;
            }
            return { ...prev, parts };
        });
    };

    // ── Exam Appearance handlers (admin only) ──
    const handleExamAppearanceChange = (index: number, field: keyof ExamAppearance, value: string) => {
        const appearances = [...(form.appearedInExams || [])];
        appearances[index] = { ...appearances[index], [field]: value };
        setForm(prev => ({ ...prev, appearedInExams: appearances }));
    };

    const handleExamAppearanceMarks = (index: number, marks: number | undefined) => {
        const appearances = [...(form.appearedInExams || [])];
        appearances[index] = { ...appearances[index], marks };
        setForm(prev => ({ ...prev, appearedInExams: appearances }));
    };

    const addExamAppearance = () => {
        const appearances = [...(form.appearedInExams || [])];
        appearances.push({ exam: 'JEE', date: '', session: '', questionNo: '' });
        setForm(prev => ({ ...prev, appearedInExams: appearances }));
    };

    const removeExamAppearance = (index: number) => {
        const appearances = [...(form.appearedInExams || [])];
        appearances.splice(index, 1);
        setForm(prev => ({ ...prev, appearedInExams: appearances }));
    };

    // A caller's allowedSubjects is honoured from every subject with a question
    // bank (QUESTION_SUBJECT_OPTIONS — the core four plus Geography): a teacher passes
    // their profile subjects, the admin console passes the whole list. Without one the
    // picker stays the core four, so Geography is never offered by default.
    const offered: readonly string[] = allowedSubjects && allowedSubjects.length > 0
        ? QUESTION_SUBJECT_OPTIONS.filter(s => allowedSubjects.includes(s))
        : SUBJECT_OPTIONS;
    // The question's own subject is always offered. A no-content course (AP
    // Calculus AB) is in neither list, and a picker without it would save the
    // question as whichever option came first.
    const subjects = form.subject && !offered.includes(form.subject) ? [...offered, form.subject] : offered;

    const renderSourceCrop = () => {
        if (!cropSlot || !source?.available || !sourceApi || !questionId) return null;
        // The form's region wins over the loaded one: it is what Save will write.
        const recorded = regionForSlot(form.sourceRegions, cropSlot) ?? regionForSlot(source.regions, cropSlot);
        const anyRegion = form.sourceRegions?.find(r => r.questionSetId === source.questionSetId) ?? source.regions[0];
        return (
            <SourceCropModal
                title={recorded ? 'Adjust crop from paper' : 'Take image from paper'}
                pages={source.pages}
                initialPage={recorded?.page ?? anyRegion?.page}
                initialRect={recorded?.rect}
                onClose={() => setCropSlot(null)}
                onApply={async (page, rect) => {
                    const result = await sourceApi.cropFromSource(questionId, {
                        slot: cropSlot, questionSetId: source.questionSetId, page, rect,
                    });
                    setForm(prev => applySlotImage(prev, result));
                    setCropSlot(null);
                }}
            />
        );
    };

    return (
        <QuestionSourceContext.Provider value={sourceContext}>
        <div className="edit-form">
            <div className="edit-form-top-actions">
                <div className="edit-form-top-left">
                    {isAdmin && onFixLatex && (
                        <button className="btn-fix-latex" onClick={handleFixLatex} disabled={fixingLatex || saving}>
                            {fixingLatex ? 'Fixing...' : 'Fix LaTeX'}
                        </button>
                    )}
                    {showPublish && onPublish && (
                        <button className="btn-publish" onClick={onPublish} disabled={saving}>
                            Publish
                        </button>
                    )}
                    {showUnpublish && onUnpublish && (
                        <button className="btn-unpublish" onClick={onUnpublish} disabled={saving}>
                            Move to Draft
                        </button>
                    )}
                </div>
                {renderTopCenter && <div className="edit-form-top-center">{renderTopCenter()}</div>}
                <div className="edit-form-top-right">
                    {saveError && (
                        <span className="edit-form-save-error" role="alert">{saveError}</span>
                    )}
                    <button className="btn-cancel" onClick={onCancel}>Cancel</button>
                    <button className="btn-save" onClick={() => onSave(withoutEmptyFigures(form))} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            <MetadataSection
                form={form}
                updateField={updateField}
                isAdmin={isAdmin}
                teacherId={teacherId}
                teacherEmail={teacherEmail}
                subjects={subjects}
                modulesBySubject={modulesBySubject}
                addExamAppearance={addExamAppearance}
                removeExamAppearance={removeExamAppearance}
                handleExamAppearanceChange={handleExamAppearanceChange}
                handleExamAppearanceMarks={handleExamAppearanceMarks}
                conceptOptions={onLoadConcepts ? conceptOptions : undefined}
                renderCurriculaPreview={renderCurriculaPreview}
            />

            <QuestionContentSection
                form={form}
                updateField={updateField}
                onUploadImage={onUploadImage}
                addPart={addPart}
                removePart={removePart}
                updatePart={updatePart}
                onRequirementChange={onRequirementChange}
            />

            <SolutionsHintsSection
                form={form}
                updateField={updateField}
                isAdmin={isAdmin}
                onUploadImage={onUploadImage}
                addSolution={addSolution}
                removeSolution={removeSolution}
                handleSolutionChange={handleSolutionChange}
                conceptOptions={onLoadConcepts ? conceptOptions : undefined}
                handleSolutionConceptsChange={handleSolutionConceptsChange}
                bookOptions={onLoadBooks ? bookOptions : undefined}
            />

            {/* Not admin-only: a teacher authoring or reviewing a question has the
                same reason to link a prerequisite or a harder variant, and the
                generated-variation link they most need to see lives here too.
                Gated on `renderQuestionPicker` alone — a host that supplies no
                picker cannot offer the "add" flow. */}
            {renderQuestionPicker && (
                <RelatedQuestionsSection
                    value={form.relatedQuestions || []}
                    onChange={next => updateField('relatedQuestions', next)}
                    onGetQuestionById={onGetQuestionById}
                    questionHref={questionHref}
                    renderQuestionPicker={renderQuestionPicker}
                />
            )}

            {historyApi && questionId && (
                <QuestionHistoryPanel
                    questionId={questionId}
                    historyApi={historyApi}
                    onRestored={onHistoryRestored}
                />
            )}
            {renderSourceCrop()}
        </div>
        </QuestionSourceContext.Provider>
    );
}

/** A figure added but never given an image is not saved — the backend requires
 *  a URL, and an empty one would render as a broken image. Its marker, if the
 *  author wrote one, stays and the editor shows it as naming no figure. */
function withoutEmptyFigures(form: QuestionFormData): QuestionFormData {
    const keep = <T extends { figures?: { url: string }[] }>(x: T): T =>
        x.figures?.some(f => !f.url) ? { ...x, figures: x.figures.filter(f => !!f.url) } : x;
    return { ...keep(form), ...(form.parts ? { parts: form.parts.map(keep) } : {}) };
}
